import i18next from 'i18next';
import type { core } from 'zod';

const translateZod = (key: string, params?: Record<string, unknown>): string => {
  if (params) {
    // @ts-expect-error - i18nextの型定義が厳密すぎるため、型アサーションを使用
    return i18next.t(key, { ...params, ns: 'zod' });
  }
  // @ts-expect-error - i18nextの型定義が厳密すぎるため、型アサーションを使用
  return i18next.t(key, { ns: 'zod' });
};

/**
 * Zod の「必須」バリデーションメッセージを返す。
 *
 * superRefine など customErrorMap でカバーされない箇所で
 * 「必須です。」を直書きせず、zod 名前空間の単一ソースを参照するために使用する。
 */
export const requiredFieldMessage = (): string =>
  translateZod('errors.invalid_type_received_undefined');

const translateTypeLabel = (typeKey: string): string => {
  if (!typeKey) {
    return typeKey;
  }
  const translationKey = `types.${typeKey}`;
  const translated = translateZod(translationKey);
  return translated === translationKey ? typeKey : translated;
};

const getReceivedTypeKey = (value: unknown): string => {
  if (value === undefined) {
    return 'undefined';
  }
  if (value === null) {
    return 'null';
  }
  if (value instanceof Date) {
    return 'date';
  }
  if (Array.isArray(value)) {
    return 'array';
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'integer' : 'float';
  }
  if (typeof value === 'bigint') {
    return 'number';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  return typeof value;
};

type RangeIssueMeta = Partial<
  Pick<Extract<core.$ZodIssue, { code: 'too_small' | 'too_big' }>, 'origin' | 'exact' | 'inclusive'>
>;

const getRangeOriginKey = (
  origin: RangeIssueMeta['origin'],
): 'array' | 'date' | 'number' | 'set' | 'string' => {
  if (origin === 'array') {
    return 'array';
  }
  if (origin === 'set') {
    return 'set';
  }
  if (origin === 'date') {
    return 'date';
  }
  if (origin === 'string') {
    return 'string';
  }
  return 'number';
};

const getRangeSeverityKey = ({
  exact,
  inclusive,
}: RangeIssueMeta): 'exact' | 'inclusive' | 'not_inclusive' => {
  if (exact) {
    return 'exact';
  }
  if (inclusive === false) {
    return 'not_inclusive';
  }
  return 'inclusive';
};

const formatNumericValue = (value: number | bigint): string | number => {
  return typeof value === 'bigint' ? value.toString() : value;
};

// Zodのissueをi18next上のzod.jsonに紐づけた日本語メッセージへ変換
export const customErrorMap: core.$ZodErrorMap = (issue) => {
  switch (issue.code) {
    case 'invalid_type': {
      const receivedTypeKey = getReceivedTypeKey(issue.input);
      if (receivedTypeKey === 'undefined') {
        return { message: translateZod('errors.invalid_type_received_undefined') };
      }
      if (receivedTypeKey === 'null') {
        return { message: translateZod('errors.invalid_type_received_null') };
      }

      const expectedKey = typeof issue.expected === 'string' ? issue.expected : 'string';
      return {
        message: translateZod('errors.invalid_type', {
          expected: translateTypeLabel(expectedKey),
          received: translateTypeLabel(receivedTypeKey),
        }),
      };
    }

    case 'invalid_format':
      if (issue.format === 'email') {
        return {
          message: translateZod('errors.invalid_string.email', { validation: 'メールアドレス' }),
        };
      }
      if (issue.format === 'url') {
        return { message: translateZod('errors.invalid_string.url', { validation: 'URL' }) };
      }
      return { message: translateZod('errors.custom') };

    case 'too_small': {
      const originKey = getRangeOriginKey(issue.origin);
      const severityKey = getRangeSeverityKey(issue);

      // 文字列のmin(1)・配列のmin(1)は「必須です」と表示
      if ((originKey === 'string' || originKey === 'array') && issue.minimum === 1) {
        return { message: translateZod('errors.invalid_type_received_undefined') };
      }

      return {
        message: translateZod(`errors.too_small.${originKey}.${severityKey}`, {
          minimum: formatNumericValue(issue.minimum),
        }),
      };
    }

    case 'too_big': {
      const originKey = getRangeOriginKey(issue.origin);
      const severityKey = getRangeSeverityKey(issue);
      return {
        message: translateZod(`errors.too_big.${originKey}.${severityKey}`, {
          maximum: formatNumericValue(issue.maximum),
        }),
      };
    }

    case 'invalid_value': {
      const receivedTypeKey = getReceivedTypeKey(issue.input);
      if (receivedTypeKey === 'undefined' || receivedTypeKey === 'null') {
        return { message: translateZod('errors.invalid_type_received_undefined') };
      }
      return { message: translateZod('errors.custom') };
    }

    default:
      return { message: translateZod('errors.custom') };
  }
};
